/**
 * Enhanced PWA Hook v2.0
 * Provides comprehensive PWA functionality including:
 * - Install prompt management
 * - Auto-update detection and handling
 * - Offline status monitoring
 * - Service worker lifecycle management
 * - Background sync registration
 * - Push notification support
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface UpdateInfo {
  available: boolean;
  version?: string;
  timestamp?: number;
}

interface StorageInfo {
  usage: number;
  quota: number;
  percentage: number;
  persisted: boolean;
}

interface PWAState {
  // Installation
  isInstallable: boolean;
  isInstalled: boolean;
  promptInstall: () => Promise<boolean>;
  
  // Online status
  isOnline: boolean;
  connectionType: string | null;
  
  // Updates
  isUpdateAvailable: boolean;
  updateInfo: UpdateInfo;
  updateServiceWorker: () => void;
  checkForUpdates: () => Promise<boolean>;
  
  // Service Worker
  swRegistration: ServiceWorkerRegistration | null;
  swState: 'installing' | 'installed' | 'activating' | 'activated' | 'redundant' | null;
  
  // Storage
  storageInfo: StorageInfo | null;
  requestPersistentStorage: () => Promise<boolean>;
  
  // Notifications
  notificationPermission: NotificationPermission;
  requestNotificationPermission: () => Promise<boolean>;
  
  // Utilities
  isPWASupported: boolean;
  displayMode: 'browser' | 'standalone' | 'minimal-ui' | 'fullscreen';
  clearAppCache: () => Promise<void>;
  getAppVersion: () => Promise<string | null>;
}

export const usePWA = (): PWAState => {
  // Installation state
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  
  // Online state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [connectionType, setConnectionType] = useState<string | null>(null);
  
  // Update state
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo>({ available: false });
  
  // Service worker state
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [swState, setSwState] = useState<PWAState['swState']>(null);
  
  // Storage state
  const [storageInfo, setStorageInfo] = useState<StorageInfo | null>(null);
  
  // Notification state
  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  
  // Display mode
  const [displayMode, setDisplayMode] = useState<PWAState['displayMode']>('browser');
  
  // Refs for cleanup
  const updateCheckInterval = useRef<NodeJS.Timeout | null>(null);
  const waitingWorker = useRef<ServiceWorker | null>(null);
  const cleanupFunctionsRef = useRef<(() => void)[]>([]);
  const hasInitialized = useRef(false); // Prevent double init in StrictMode

  // ==================== INITIALIZATION ====================
  
  useEffect(() => {
    // Prevent double initialization in StrictMode
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    
    // Check PWA support
    const isPWASupported = 'serviceWorker' in navigator;
    
    if (!isPWASupported) {
      console.log('[PWA] Service Worker not supported');
      return;
    }

    // Initialize all PWA features
    initializePWA();
    
    // Listen for new deployment detection from service worker
    const handleNewDeployment = (event: MessageEvent) => {
      if (event.data?.type === 'NEW_DEPLOYMENT_DETECTED') {
        console.log('[PWA] New deployment detected from SW:', event.data);
        setIsUpdateAvailable(true);
        setUpdateInfo({
          available: true,
          version: event.data.version,
          timestamp: event.data.timestamp
        });
      }
    };
    
    navigator.serviceWorker.addEventListener('message', handleNewDeployment);

    return () => {
      // Cleanup all registered functions
      cleanupFunctionsRef.current.forEach(cleanup => cleanup());
      cleanupFunctionsRef.current = [];
      
      // Cleanup update check interval
      if (updateCheckInterval.current) {
        clearInterval(updateCheckInterval.current);
      }
      
      navigator.serviceWorker.removeEventListener('message', handleNewDeployment);
    };
  }, []);

  const initializePWA = async () => {
    // Check if already installed
    checkInstallationStatus();
    
    // Check display mode
    checkDisplayMode();
    
    // Setup event listeners and store cleanup function
    const eventListenersCleanup = setupEventListeners();
    if (eventListenersCleanup) {
      cleanupFunctionsRef.current.push(eventListenersCleanup);
    }
    
    // Register/update service worker
    await registerServiceWorker();
    
    // Check storage
    await updateStorageInfo();
    
    // Check network and store cleanup function
    const connectionCleanup = updateConnectionInfo();
    if (connectionCleanup) {
      cleanupFunctionsRef.current.push(connectionCleanup);
    }
    
    // Setup periodic update checks (every 5 minutes for faster Wasmer deployment detection)
    updateCheckInterval.current = setInterval(checkForUpdates, 5 * 60 * 1000);
    
    // Initial update check after 3 seconds
    setTimeout(checkForUpdates, 3000);
  };

  // ==================== INSTALLATION ====================

  const checkInstallationStatus = () => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = (window.navigator as any).standalone === true;
    setIsInstalled(isStandalone || isIOSStandalone);
  };

  const checkDisplayMode = () => {
    if (window.matchMedia('(display-mode: fullscreen)').matches) {
      setDisplayMode('fullscreen');
    } else if (window.matchMedia('(display-mode: standalone)').matches) {
      setDisplayMode('standalone');
    } else if (window.matchMedia('(display-mode: minimal-ui)').matches) {
      setDisplayMode('minimal-ui');
    } else {
      setDisplayMode('browser');
    }
  };

  const setupEventListeners = () => {
    // Install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
      console.log('[PWA] Install prompt available');
    };

    // App installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      console.log('[PWA] App installed successfully');
      
      // Track installation
      localStorage.setItem('pwa_installed', 'true');
      localStorage.setItem('pwa_install_date', new Date().toISOString());
    };

    // Online/offline status
    const handleOnline = () => {
      setIsOnline(true);
      // Update connection type without adding new listener (listener already exists from init)
      const connection = (navigator as any).connection || 
                         (navigator as any).mozConnection || 
                         (navigator as any).webkitConnection;
      if (connection) {
        setConnectionType(connection.effectiveType || connection.type || null);
      }
      console.log('[PWA] Back online');
      
      // Trigger background sync if available
      if (swRegistration && 'sync' in swRegistration) {
        (swRegistration as any).sync.register('sync-sessions').catch(console.error);
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('[PWA] Gone offline');
    };

    // Display mode change
    const handleDisplayModeChange = () => {
      checkDisplayMode();
      checkInstallationStatus();
    };

    // Service worker messages
    const handleSWMessage = (event: MessageEvent) => {
      const { type, version, timestamp } = event.data || {};
      
      switch (type) {
        case 'SW_UPDATED':
          console.log('[PWA] Service worker updated to version:', version);
          setIsUpdateAvailable(true);
          setUpdateInfo({ available: true, version, timestamp });
          break;
          
        case 'SYNC_SESSIONS':
          // Trigger session sync
          window.dispatchEvent(new CustomEvent('pwa:sync-sessions'));
          break;
          
        case 'PERIODIC_SYNC':
          // Handle periodic sync
          window.dispatchEvent(new CustomEvent('pwa:periodic-sync'));
          break;
          
        case 'NOTIFICATION_CLICK':
          // Handle notification click
          window.dispatchEvent(new CustomEvent('pwa:notification-click', { detail: event.data }));
          break;
      }
    };

    // Add listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Display mode change listener
    const displayModeMediaQuery = window.matchMedia('(display-mode: standalone)');
    displayModeMediaQuery.addEventListener('change', handleDisplayModeChange);
    
    // Service worker message listener
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('[PWA] Controller changed - new service worker active');
        // Optionally reload to get latest version
        // window.location.reload();
      });
    }

    // Cleanup function stored for later
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      displayModeMediaQuery.removeEventListener('change', handleDisplayModeChange);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  };

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!deferredPrompt) {
      console.log('[PWA] No install prompt available');
      return false;
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`[PWA] User ${outcome} the install prompt`);
      
      if (outcome === 'accepted') {
        setIsInstallable(false);
        return true;
      }
      
      setDeferredPrompt(null);
      return false;
    } catch (error) {
      console.error('[PWA] Install prompt error:', error);
      return false;
    }
  }, [deferredPrompt]);

  // ==================== SERVICE WORKER ====================

  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) return;

    try {
      // Unregister old service workers that might be cached
      const registrations = await navigator.serviceWorker.getRegistrations();
      
      // Register our service worker
      const registration = await navigator.serviceWorker.register('/sw.js', {
        updateViaCache: 'none'
      });
      
      setSwRegistration(registration);

      // Track service worker state
      const trackState = (sw: ServiceWorker | null) => {
        if (!sw) return;
        
        setSwState(sw.state as PWAState['swState']);
        
        sw.addEventListener('statechange', () => {
          setSwState(sw.state as PWAState['swState']);
          
          if (sw.state === 'installed' && navigator.serviceWorker.controller) {
            setIsUpdateAvailable(true);
            waitingWorker.current = sw;
          }
        });
      };

      // Track current installing/waiting/active workers
      if (registration.installing) {
        trackState(registration.installing);
      }
      if (registration.waiting) {
        setIsUpdateAvailable(true);
        waitingWorker.current = registration.waiting;
        trackState(registration.waiting);
      }
      if (registration.active) {
        trackState(registration.active);
      }

      // Listen for new workers
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        trackState(newWorker);
      });

      // Register background sync if available
      if ('sync' in registration) {
        try {
          await (registration as any).sync.register('sync-sessions');
        } catch (err) {
          // Silently fail - background sync is optional
        }
      }

      // Register periodic background sync if available
      if ('periodicSync' in registration) {
        try {
          const status = await navigator.permissions.query({
            name: 'periodic-background-sync' as any
          });
          
          if (status.state === 'granted') {
            await (registration as any).periodicSync.register('content-sync', {
              minInterval: 24 * 60 * 60 * 1000 // 24 hours
            });
          }
        } catch (err) {
          // Silently fail - periodic sync is optional
        }
      }

    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
    }
  };

  const updateServiceWorker = useCallback(async () => {
    try {
      if (waitingWorker.current) {
        console.log('[PWA] Activating waiting service worker...');
        
        // Tell the waiting worker to skip waiting
        waitingWorker.current.postMessage({ type: 'SKIP_WAITING' });
        
        // Listen for controllerchange and reload
        const controllerChanged = new Promise<void>((resolve) => {
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            console.log('[PWA] Controller changed, reloading...');
            resolve();
          }, { once: true });
        });
        
        // Wait for controller change or timeout
        await Promise.race([
          controllerChanged,
          new Promise(resolve => setTimeout(resolve, 2000))
        ]);
        
        window.location.reload();
      } else if (swRegistration) {
        console.log('[PWA] No waiting worker, forcing update check...');
        
        // Force update check
        await swRegistration.update();
        
        // Check if there's now a waiting worker
        if (swRegistration.waiting) {
          swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
          
          // Wait a bit then reload
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        window.location.reload();
      } else {
        console.log('[PWA] No service worker registration, just reloading...');
        window.location.reload();
      }
    } catch (error) {
      console.error('[PWA] Update failed:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  }, [swRegistration]);

  const checkForUpdates = useCallback(async (): Promise<boolean> => {
    if (!swRegistration) return false;

    try {
      await swRegistration.update();
      
      // Check if there's a waiting worker
      if (swRegistration.waiting) {
        setIsUpdateAvailable(true);
        waitingWorker.current = swRegistration.waiting;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[PWA] Update check failed:', error);
      return false;
    }
  }, [swRegistration]);

  // ==================== STORAGE ====================

  const updateStorageInfo = async () => {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      try {
        const estimate = await navigator.storage.estimate();
        const persisted = await navigator.storage.persisted();
        
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 0;
        const percentage = quota > 0 ? (usage / quota) * 100 : 0;

        setStorageInfo({
          usage: Math.round(usage / 1024 / 1024 * 100) / 100, // MB
          quota: Math.round(quota / 1024 / 1024 * 100) / 100, // MB
          percentage: Math.round(percentage * 100) / 100,
          persisted
        });
      } catch (error) {
        console.error('[PWA] Failed to get storage info:', error);
      }
    }
  };

  const requestPersistentStorage = useCallback(async (): Promise<boolean> => {
    if ('storage' in navigator && 'persist' in navigator.storage) {
      try {
        const isPersisted = await navigator.storage.persist();
        console.log(`[PWA] Persistent storage: ${isPersisted ? 'granted' : 'denied'}`);
        await updateStorageInfo();
        return isPersisted;
      } catch (error) {
        console.error('[PWA] Failed to request persistent storage:', error);
        return false;
      }
    }
    return false;
  }, []);

  // ==================== NOTIFICATIONS ====================

  const requestNotificationPermission = useCallback(async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.log('[PWA] Notifications not supported');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      console.log(`[PWA] Notification permission: ${permission}`);
      return permission === 'granted';
    } catch (error) {
      console.error('[PWA] Notification permission request failed:', error);
      return false;
    }
  }, []);

  // ==================== NETWORK ====================

  const updateConnectionInfo = (): (() => void) | undefined => {
    const connection = (navigator as any).connection || 
                       (navigator as any).mozConnection || 
                       (navigator as any).webkitConnection;
    
    if (connection) {
      setConnectionType(connection.effectiveType || connection.type || null);
      
      // Create named handler for cleanup
      const handleConnectionChange = () => {
        setConnectionType(connection.effectiveType || connection.type || null);
      };
      
      // Listen for connection changes
      connection.addEventListener('change', handleConnectionChange);
      
      // Return cleanup function
      return () => {
        connection.removeEventListener('change', handleConnectionChange);
      };
    }
    
    return undefined;
  };

  // ==================== UTILITIES ====================

  const clearAppCache = useCallback(async (): Promise<void> => {
    if (!('caches' in window)) return;

    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('[PWA] All caches cleared');
      
      // Optionally unregister service worker
      if (swRegistration) {
        await swRegistration.unregister();
        window.location.reload();
      }
    } catch (error) {
      console.error('[PWA] Failed to clear cache:', error);
    }
  }, [swRegistration]);

  const getAppVersion = useCallback(async (): Promise<string | null> => {
    if (!swRegistration?.active) return null;

    return new Promise((resolve) => {
      const channel = new MessageChannel();
      channel.port1.onmessage = (event) => {
        resolve(event.data?.version || null);
      };
      
      swRegistration.active.postMessage(
        { type: 'GET_VERSION' },
        [channel.port2]
      );
      
      // Timeout after 5 seconds
      setTimeout(() => resolve(null), 5000);
    });
  }, [swRegistration]);

  return {
    // Installation
    isInstallable,
    isInstalled,
    promptInstall,
    
    // Online status
    isOnline,
    connectionType,
    
    // Updates
    isUpdateAvailable,
    updateInfo,
    updateServiceWorker,
    checkForUpdates,
    
    // Service Worker
    swRegistration,
    swState,
    
    // Storage
    storageInfo,
    requestPersistentStorage,
    
    // Notifications
    notificationPermission,
    requestNotificationPermission,
    
    // Utilities
    isPWASupported: 'serviceWorker' in navigator,
    displayMode,
    clearAppCache,
    getAppVersion
  };
};

export default usePWA;
